import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SearchBar from "@/components/SearchBar";
import FilterButtons from "@/components/FilterButtons";
import EpisodeCard from "@/components/EpisodeCard";
import EpisodeModal from "@/components/EpisodeModal";
import Sidebar from "@/components/Sidebar";
import SidebarToggle from "@/components/SidebarToggle";
import { usePodcast } from "@/contexts/PodcastContext";
import { useEpisodes, useEpisodeTags, type Episode } from "@/hooks/useEpisodes";
import { cn } from "@/lib/utils";
import type { FormatTag, ThemeTag, TopicTag } from "@/lib/constants";
import { theme } from "@/lib/theme";
import { isPodcastProcessing } from "@/lib/podcast-utils";

export default function EpisodesPage() {
  const {
    selectedPodcast,
    availablePodcasts,
    isLoading: podcastLoading,
  } = usePodcast();

  // Use SWR hooks for data fetching
  const { episodes, isLoading: episodesLoading } = useEpisodes(selectedPodcast?.id);
  const { formatTags, themeTags, topicTags } = useEpisodeTags(selectedPodcast?.id, selectedPodcast?.taxonomy);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFormat, setSelectedFormat] = useState<FormatTag | null>(null);
  const [selectedThemes, setSelectedThemes] = useState<ThemeTag[]>([]);
  const [selectedTopics, setSelectedTopics] = useState<TopicTag[]>([]);
  const [selectedEpisode, setSelectedEpisode] = useState<Episode | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const isLoading = episodesLoading;

  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth >= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Reset filters when podcast changes
  useEffect(() => {
    setSelectedFormat(null);
    setSelectedThemes([]);
    setSelectedTopics([]);
    setSearchQuery("");
  }, [selectedPodcast?.id]);

  const filteredEpisodes = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return episodes.filter(episode => {
      if (query) {
        const matchesSearch =
          episode.title.toLowerCase().includes(query) ||
          episode.description.toLowerCase().includes(query) ||
          (episode.cleanedDescription?.toLowerCase().includes(query) ?? false);
        if (!matchesSearch) return false;
      }

      if (selectedFormat && !episode.formatTags.includes(selectedFormat)) {
        return false;
      }

      if (selectedThemes.length > 0) {
        const hasAllThemes = selectedThemes.every(theme => episode.themeTags.includes(theme));
        if (!hasAllThemes) return false;
      }

      if (selectedTopics.length > 0) {
        const hasAllTopics = selectedTopics.every(topic => episode.topicTags.includes(topic));
        if (!hasAllTopics) return false;
      }

      return true;
    });
  }, [episodes, searchQuery, selectedFormat, selectedThemes, selectedTopics]);

  const handleFormatChange = (format: FormatTag) => {
    setSelectedFormat(selectedFormat === format ? null : format);
  };

  const handleThemeChange = (theme: ThemeTag) => {
    setSelectedThemes(prev => (prev.includes(theme) ? prev.filter(t => t !== theme) : [...prev, theme]));
  };

  const handleTopicChange = (topic: TopicTag) => {
    setSelectedTopics(prev => (prev.includes(topic) ? prev.filter(t => t !== topic) : [...prev, topic]));
  };

  const noPodcasts = !podcastLoading && availablePodcasts.length === 0;
  const missingSelection = !podcastLoading && availablePodcasts.length > 0 && !selectedPodcast;
  const showInitialLoading = podcastLoading;

  if (showInitialLoading) {
    return (
      <div className={cn("min-h-screen transition-colors duration-200 flex flex-col", theme.colors.backgroundCream)}>
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 py-16">
          <div className="text-xl text-gray-600 dark:text-gray-300">Loading episodes...</div>
        </main>
        <Footer />
      </div>
    );
  }

  if (noPodcasts) {
    return (
      <div className={cn("min-h-screen transition-colors duration-200 flex flex-col", theme.colors.backgroundCream)}>
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 py-16">
          <div className={cn("max-w-2xl w-full text-center space-y-6 px-8 py-10", theme.cards.base)}>
            <div
              className={cn(
                "mx-auto w-16 h-16 flex items-center justify-center rounded-full",
                theme.colors.primaryOrange,
                theme.borders.neubrutalism,
                theme.shadows.neubrutalismSm,
              )}
            >
              <span className="text-3xl">🎧</span>
            </div>
            <div className="space-y-3">
              <h2 className="text-3xl font-black text-gray-900 dark:text-white">No podcasts added yet</h2>
              <p className="text-lg text-gray-700 dark:text-gray-300">
                Add a podcast to start browsing episodes. You can always switch between podcasts later.
              </p>
            </div>
            <div className="flex justify-center">
              <Link
                to="/podcasts/add"
                className={cn(theme.buttons.primary, "inline-flex px-6 py-3")}
              >
                Add a podcast
              </Link>
            </div>
            <div className="flex items-center gap-4 text-gray-500 dark:text-gray-400">
              <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600" />
              <span className="text-sm font-medium">OR</span>
              <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600" />
            </div>
            <p className="text-gray-700 dark:text-gray-300">
              <Link
                to="/try"
                className={cn("font-semibold hover:underline", theme.text.primaryOrange)}
              >
                Chat with up to 5 podcast episodes
              </Link>{" "}
              without subscribing.
            </p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (missingSelection) {
    return (
      <div className={cn("min-h-screen transition-colors duration-200 flex flex-col", theme.colors.backgroundCream)}>
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 py-16">
          <div className="text-xl text-gray-700 dark:text-gray-300">Select a podcast to view episodes.</div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.colors.backgroundCream} transition-colors duration-200 flex flex-col`}>
      <Header />

      <Sidebar
        isOpen={isSidebarOpen}
        selectedThemes={selectedThemes}
        selectedTopics={selectedTopics}
        onThemeChange={handleThemeChange}
        onTopicChange={handleTopicChange}
        onClose={() => setIsSidebarOpen(false)}
        themeTags={themeTags}
        topicTags={topicTags}
      />

      <main className="flex-1 transition-all duration-300">
        <div className={`max-w-7xl mx-auto px-4 py-8 transition-all duration-300 ${isSidebarOpen ? "md:ml-[300px]" : ""}`}>
          <div className="flex items-center gap-4 mb-8">
            <SidebarToggle isOpen={isSidebarOpen} onClick={() => setIsSidebarOpen(prev => !prev)} />
            <div className="flex-grow">
              <SearchBar value={searchQuery} onSearch={setSearchQuery} />
            </div>
          </div>

          <div className="mb-8">
            <FilterButtons
              selectedFormat={selectedFormat}
              onFormatChange={handleFormatChange}
              formatTags={formatTags}
              showOnlyFormat
            />
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-xl text-gray-600 dark:text-gray-400">Loading episodes...</div>
            </div>
          ) : filteredEpisodes.length === 0 && !searchQuery && !selectedFormat && selectedThemes.length === 0 && selectedTopics.length === 0 ? (
            isPodcastProcessing(selectedPodcast, episodes) ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div
                  className={cn(
                    "w-24 h-24 rounded-full flex items-center justify-center mb-6",
                    theme.colors.primaryOrange,
                    theme.borders.neubrutalism,
                    theme.shadows.neubrutalismSm,
                  )}
                >
                  <span className="w-12 h-12 border-4 border-gray-900 dark:border-gray-300 border-t-transparent rounded-full animate-spin" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Processing Your Podcast
                </h2>
                <p className="text-gray-600 dark:text-gray-400 max-w-md mb-4">
                  We're transcribing and indexing episodes from <strong>{selectedPodcast?.name}</strong>. This can take up to 24 hours.
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  You'll receive an email when your podcast is ready.
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-6">
                  <span className="text-4xl">🎙️</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Welcome to PodWise!
                </h2>
                <p className="text-gray-600 dark:text-gray-400 max-w-md mb-8">
                  It looks like you haven't added any podcasts yet. Add your first podcast to start chatting with episodes.
                </p>
                <a
                  href="/podcasts/add"
                  className={`px-6 py-3 rounded-xl font-semibold ${theme.buttons.primary}`}
                >
                  Add Your First Podcast
                </a>
              </div>
            )
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredEpisodes.map(episode => (
                <EpisodeCard key={episode.guid} episode={episode} onClick={() => setSelectedEpisode(episode)} />
              ))}
            </div>
          )}
        </div>

        {selectedEpisode && <EpisodeModal episode={selectedEpisode} onClose={() => setSelectedEpisode(null)} />}
      </main>

      <Footer />
    </div>
  );
}
